/**
 * Command Registry
 *
 * Aggregates all commands and provides filtered access.
 *
 * Besides the command palette, the native application menu
 * (`electron/appMenuTemplate.ts`) addresses commands by id: a menu click is
 * pushed to the renderer over `menu:command` and resolved here via
 * `executeCommand`. `electron/appMenuTemplate.antiDrift.test.ts` cross-checks
 * that every id the menu references still exists in this registry.
 */

import type { CommandDefinition } from '../types/commandTypes';
import { workspaceCommands } from './workspaceCommands';
import { ddlCommands } from './ddlCommands';
import { navigationCommands } from './navigationCommands';
import { settingsCommands } from './settingsCommands';
import { connectionCommands } from './connectionCommands';
import { navigationalRenameCommands } from './navigationalRenameCommands';

/** All registered commands from all categories */
const allCommands: CommandDefinition[] = [
  ...workspaceCommands,
  ...ddlCommands,
  ...navigationCommands,
  ...settingsCommands,
  ...connectionCommands,
  ...navigationalRenameCommands,
];

/** Get all commands that are currently visible */
export function getVisibleCommands(): CommandDefinition[] {
  return allCommands.filter((cmd) => !cmd.isVisible || cmd.isVisible());
}

/** Get all registered commands (including hidden) */
export function getAllCommands(): CommandDefinition[] {
  return [...allCommands];
}

/** Look up a single command by its id. Returns undefined when unknown. */
export function getCommandById(id: string): CommandDefinition | undefined {
  return allCommands.find((cmd) => cmd.id === id);
}

/**
 * Pure core of `executeCommand`, kept separate so the gating rules can be
 * unit-tested against fabricated commands.
 *
 * Contract:
 *  - unknown id      -> `console.warn` + no-op, returns false (never throws;
 *                       a stale menu entry must not crash the renderer)
 *  - gated off       -> no-op, returns false (`isEnabled`/`isVisible` are the
 *                       same gates the palette honours, so a menu item that
 *                       lies about availability degrades to nothing)
 *  - otherwise       -> runs `execute()` and returns true. A SYNCHRONOUS throw
 *                       from `execute()` is deliberately NOT caught — a broken
 *                       command must surface, not disappear. `execute` is typed
 *                       `void | Promise<void>` though, and a rejected promise
 *                       cannot propagate to this (sync) caller, so an async
 *                       failure is logged rather than left as an unhandled
 *                       rejection.
 */
export function runCommandById(commands: readonly CommandDefinition[], id: string): boolean {
  const cmd = commands.find((c) => c.id === id);
  if (!cmd) {
    console.warn(`[commandRegistry] Unknown command id: ${id}`);
    return false;
  }
  if (cmd.isVisible && !cmd.isVisible()) return false;
  if (cmd.isEnabled && !cmd.isEnabled()) return false;
  // A synchronous throw escapes before the `.then` probe below, which is what
  // we want; only the async tail is adopted and surfaced.
  const result = cmd.execute();
  if (result && typeof (result as Promise<void>).then === 'function') {
    void (result as Promise<void>).catch((error) => {
      console.error(`[commandRegistry] Command failed: ${id}`, error);
    });
  }
  return true;
}

/** Execute a registered command by id. See `runCommandById` for the contract. */
export function executeCommand(id: string): boolean {
  return runCommandById(allCommands, id);
}
